import {
  DataBlockDictionaryItemDto,
  NumberSource,
  PatientDto,
  PatientEgkCardData,
  QueryFactory,
} from '@lmt/shared-types';
import { CardReadingResult } from '@lmt/shared-types/src/egk-card/PatientEgkCardData';

import { CustomDictionaryName } from '@lmt/unicorn-shared/app/api/custom-hooks';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import {
  FieldStatus,
  getPatientDict,
  getPatientFields,
  PatientData,
  titleFieldKey,
} from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/common';
import { getObjectHash } from '@lmt/unicorn-shared/app/components/merge-assistant/form/utils/hash-utils';
import { useDictionaryKeyTranslation } from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/helpers';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryClient } from '@tanstack/react-query';
import { getBridge } from 'lmt-patientmanager/utils/bridge';
import React, { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import styles from './CardReaderHandler.module.scss';
import { ToastIcon } from '../../common/icons/toast/ToastIcon';

export type CardData = PatientEgkCardData & {
  healthInsuranceCompanyId?: number;
  title?: number | string | null;
  values?: any; // custom fields
};

function toastError(error: any) {
  toast.dismiss();
  toast(error, {
    icon: () => <ToastIcon type="error" />,
  });
}

function toastSuccess(content: any) {
  toast(content, {
    icon: () => <ToastIcon type="success" />,
  });
}

const insuranceCompanyNumberField = 'Number';
const insuranceCompanyShortNameField = 'Short name';

export const CardReaderHandler = () => {
  const forms = useGlobalForms();
  const tabs = useTabs();
  const queryClient = useQueryClient();
  const i18next = useScopedTranslation('merge_assistant.merge_with_card_data');
  const i18nDictKey = useDictionaryKeyTranslation();

  const dictionariesQuery = QueryFactory.DataBlockDictionaryQuery.useGetQuery();

  const findPatientByNumber = useCallback(async (patientNumber: string) => {
    const patients = await QueryFactory.PatientClient.getPatients(
      undefined,
      undefined,
      undefined,
      undefined,
      patientNumber,
      NumberSource.External,
      undefined,
      undefined,
    );
    if (patients.data.length > 1) {
      throw Error(
        i18next.t('found_patient_not_single', { number: patientNumber }),
      );
    }
    return patients.data.length === 0 ? null : patients.data[0];
  }, []);

  const getOrCreateDictionaryItem = useCallback(
    async (title: string, dictionaryId: number, values?: any) => {
      const dictionaryItems =
        await QueryFactory.DataBlockDictionaryItemClient.get(
          dictionaryId,
          true,
        );
      let result = dictionaryItems.data?.find(
        (x) => i18nDictKey.t(x.value as any) === title,
      );
      if (!result) {
        result = await QueryFactory.DataBlockDictionaryItemClient.create({
          dictionaryId: dictionaryId,
          value: title,
          values: values,
        });
        await queryClient.invalidateQueries({
          queryKey:
            QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(dictionaryId),
        });
      }
      return result;
    },
    [],
  );

  const getDictByTitle = useCallback(
    async (title: string) => {
      const dictionaries =
        dictionariesQuery.data ??
        (await QueryFactory.DataBlockDictionaryClient.get());
      return dictionaries.find((dict) => dict.title === title);
    },
    [dictionariesQuery.data],
  );

  const getOrCreateInsuranceCompany = useCallback(
    async (cardData: CardData) => {
      const insuranceCompanyDictionary = await getDictByTitle(
        CustomDictionaryName.InsuranceCompanies,
      );
      if (insuranceCompanyDictionary) {
        const number = cardData.healthInsuranceCompanyNumber;
        const dictionaryItems =
          await QueryFactory.DataBlockDictionaryItemClient.get(
            insuranceCompanyDictionary.id,
            true,
            number,
          );
        const existingCompany = dictionaryItems.data.find(
          (x) => x.values[insuranceCompanyNumberField] === number,
        );
        if (existingCompany) {
          if (existingCompany.isDeleted) {
            await QueryFactory.DataBlockDictionaryItemClient.patch(
              existingCompany.id,
              {
                isDeleted: false,
              },
            );
          }
          return existingCompany;
        }

        return await QueryFactory.DataBlockDictionaryItemClient.create({
          value: cardData.healthInsuranceCompany,
          dictionaryId: insuranceCompanyDictionary.id,
          values: {
            [insuranceCompanyNumberField]:
              cardData.healthInsuranceCompanyNumber,
            [insuranceCompanyShortNameField]:
              cardData.healthInsuranceCompanyShortName,
          },
        });
      } else {
        console.error('Dictionary of insurance companies is not found');
      }
    },
    [],
  );

  const isCardHashExists = useCallback(
    async (patientId: number, cardData: CardData) => {
      const cardDataHash = getObjectHash(cardData);
      return (
        (await QueryFactory.PatientCardHashClient.get(patientId, cardDataHash))
          .length > 0
      );
    },
    [],
  );

  const CardDataAlreadyUsedToast = useCallback(
    (patient: PatientDto, card: CardData) => {
      return (
        <div className={styles.container}>
          <div className={styles.text}>
            {i18next.t('card_was_already_used')}
          </div>
          <div>
            <UniLink
              title={i18next.t('force_data_comparison')}
              color={ButtonColor.Primary}
              onClick={() =>
                forms.openMergePatientWithCardData(patient, card, true)
              }
            />
          </div>
        </div>
      );
    },
    [forms],
  );

  const InsuranceCompanyNameIgnoredToast = useCallback(
    (insuranceCompany: DataBlockDictionaryItemDto, card: CardData) => {
      return (
        <div className={styles.container}>
          <div className={styles.text}>
            {i18next.t('insurance_company_name_ignored')}
          </div>
          <div>
            <UniLink
              title={i18next.t('force_data_comparison')}
              color={ButtonColor.Primary}
              onClick={() =>
                forms.openMergeInsuranceCardData(insuranceCompany, card)
              }
            />
          </div>
        </div>
      );
    },
    [forms],
  );

  const getFilteredPatientDict = useCallback(
    (patientData: PatientData, keys: string[]) => {
      const patientDict = getPatientDict(patientData);
      return Object.fromEntries(
        Object.entries(patientDict).filter(([key]) => keys.includes(key)),
      );
    },
    [],
  );

  /**
   * Open comparison form for insurance company (if numbers are the same and names are different)
   */
  const openUpdateInsuranceDataFormIfNeeded = useCallback(
    async (
      insuranceCompany: DataBlockDictionaryItemDto,
      cardData: CardData,
    ) => {
      if (
        insuranceCompany.values[insuranceCompanyNumberField] ===
          cardData.healthInsuranceCompanyNumber &&
        insuranceCompany.value !== cardData.healthInsuranceCompany
      ) {
        const ignoredInsuranceCompanyNamesDictionary = await getDictByTitle(
          CustomDictionaryName.IgnoredInsuranceCompanyNames,
        );
        const ignoredNames =
          await QueryFactory.DataBlockDictionaryItemClient.get(
            ignoredInsuranceCompanyNamesDictionary?.id,
            true,
            cardData.healthInsuranceCompany,
          );
        if (
          ignoredNames.data.find(
            (x) => x.value === cardData.healthInsuranceCompany && !x.isDeleted,
          )
        ) {
          toastSuccess(
            InsuranceCompanyNameIgnoredToast(insuranceCompany, cardData),
          );
          return;
        }
        await forms.openMergeInsuranceCardData(insuranceCompany, cardData);
      }
    },
    [forms],
  );

  const onCardReading = useCallback(
    async (result: CardReadingResult) => {
      try {
        if (result.error) {
          toastError(result.error.message);
        }
        if (!result.data) {
          return;
        }

        const cardData: CardData = result.data;
        const foundPatient = await findPatientByNumber(cardData.number);

        if (foundPatient) {
          tabs.openTab(
            pages.patientProfile.link({
              patientId: foundPatient.id,
            }),
          );
        }

        const insuranceCompany = await getOrCreateInsuranceCompany(cardData);
        cardData.healthInsuranceCompanyId = insuranceCompany?.id;
        if (cardData.titleAsString) {
          const titleDictionary = await getDictByTitle(
            CustomDictionaryName.Titles,
          );
          if (titleDictionary) {
            const title = await getOrCreateDictionaryItem(
              cardData.titleAsString,
              titleDictionary.id,
            );
            cardData.title = title.isDeleted ? title.value : title.id; // we can't save patient with deleted dictionary item, so we replace it with string value
            cardData.values = {
              // also needs to be set as custom field if we create a new patient
              [titleFieldKey]: title.isDeleted ? title.value : title.id,
            };
          } else {
            console.error('Dictionary of person titles is not found');
          }
        } else {
          cardData.title = null;
        }

        if (!foundPatient) {
          if (insuranceCompany) {
            await openUpdateInsuranceDataFormIfNeeded(
              insuranceCompany,
              cardData,
            );
          }
          void forms.openPatientCreating(
            undefined,
            undefined,
            true,
            cardData,
            true,
          );
          return;
        }

        const patient = await QueryFactory.PatientClient.getPatient(
          foundPatient.id,
        );
        const patientData: PatientData = {
          ...patient,
          title: patient.values[titleFieldKey],
        };

        if (insuranceCompany) {
          await openUpdateInsuranceDataFormIfNeeded(insuranceCompany, cardData);
        }
        const patientDict = getFilteredPatientDict(
          patientData,
          Object.keys(cardData),
        );
        const cardDict = getPatientDict(cardData);
        const commonFields = getPatientFields(patientDict, cardDict);
        const isDataEqual =
          Object.keys(commonFields).filter(
            (key) => commonFields[key] !== FieldStatus.const,
          ).length === 0;
        if (isDataEqual) {
          toastSuccess(i18next.t('patient_data_is_equal'));
          return;
        }
        // if we find this card hash among card hashes we don't need to show form again
        if (await isCardHashExists(patient.id, cardData)) {
          toastSuccess(CardDataAlreadyUsedToast(patientData, cardData));
          return;
        }
        forms.openMergePatientWithCardData(patientData, cardData, true);
      } catch (e) {
        let errorMessage = convertToErrorString(e);
        if (errorMessage === 'Card Reader not connected') {
          return;
        }
        if (errorMessage.startsWith('value: ')) {
          errorMessage = errorMessage.slice(7);
        }
        toastError(errorMessage);
        console.error('onCardReading', e);
      }
    },
    [tabs, forms, dictionariesQuery.data],
  );

  useEffect(() => {
    const bridge = getBridge();
    const unsubscribeCardReader = bridge?.onEgkCardReading(
      async (result: CardReadingResult) => onCardReading(result),
    );

    // also we add handler to mock card reading via calling document.dispatchEvent('onEgkCardReading')
    function handleCardReading(event: Event) {
      return onCardReading((event as any).detail);
    }
    document.addEventListener('onEgkCardReading', handleCardReading);
    return () => {
      unsubscribeCardReader?.();
      document.removeEventListener('onEgkCardReading', handleCardReading);
    };
  }, []);

  return null;
};
